import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import TopBarActions from '../../components/layout/TopBarActions';
import IdentityCarousel from '../../components/IdentityCarousel';

const DRAWER_W = 260;

const PALETTE = {
  bg: {
    shell: 'linear-gradient(180deg, #f8fafc 0%, #e8f5e9 100%)',
    drawer: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(232,245,233,0.9) 100%)',
    header: 'linear-gradient(135deg, #00897b 0%, #26a69a 40%, #1976d2 100%)',
  },
  text: {
    primary: '#00695c',
    nav: '#37474f',
    muted: '#64748b',
  },
};

function getPageTitle(pathname) {
  if (pathname.includes('/pastas/')) return 'Conteúdos';
  if (pathname.includes('/setores/')) return 'Pastas do setor';
  return 'Trilha de Aprendizado';
}

function Sidebar({ location, onNavigate, onCloseMobile, unidadeLabel }) {
  const items = [
    { label: 'Setores', path: '/trilha-aprendizado', icon: <CategoryIcon /> },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          background: PALETTE.bg.header,
          color: '#fff',
        }}
      >
        <SchoolIcon />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            Trilha
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
            Aprendizado
          </Typography>
        </Box>
      </Box>
      {unidadeLabel && (
        <Chip size="small" label={unidadeLabel} sx={{ mb: 2, alignSelf: 'flex-start' }} />
      )}
      <List dense sx={{ flex: 1 }}>
        {items.map((item) => {
          const selected =
            item.path === '/trilha-aprendizado'
              ? location.pathname === '/trilha-aprendizado'
              : location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.path}
              selected={selected}
              onClick={() => {
                onNavigate(item.path);
                onCloseMobile?.();
              }}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: selected ? PALETTE.text.primary : PALETTE.text.nav }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500, color: PALETTE.text.nav }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
        Conteúdo institucional — todas as unidades
      </Typography>
    </Box>
  );
}

export default function TrilhaAprendizadoLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const unidadeLabel = 'Institucional';
  const title = getPageTitle(location.pathname);

  const drawerPaperSx = {
    width: DRAWER_W,
    border: 0,
    boxSizing: 'border-box',
    background: PALETTE.bg.drawer,
    borderRadius: '0 20px 20px 0',
    boxShadow: '0 8px 32px rgba(0,137,123,0.12)',
  };

  const sidebar = (
    <Sidebar
      location={location}
      onNavigate={navigate}
      onCloseMobile={() => setMobileOpen(false)}
      unidadeLabel={unidadeLabel}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        height: '100vh',
        background: PALETTE.bg.shell,
        overflow: 'hidden',
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: DRAWER_W, flexShrink: 0, '& .MuiDrawer-paper': drawerPaperSx }} open>
          {sidebar}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          pt: { xs: 1, sm: 1.5 },
          boxSizing: 'border-box',
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(232,245,233,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            color: '#00897b',
            mb: 0,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderBottom: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '0 0 20px 20px',
            flexShrink: 0,
          }}
        >
          <Toolbar sx={{ flexWrap: 'wrap', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 }, gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} color="primary" aria-label="Abrir menu">
                <MenuIcon />
              </IconButton>
            )}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/home')}
              size="small"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'none',
                flexShrink: 0,
              }}
            >
              Voltar ao Painel
            </Button>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {title}
            </Typography>
            <TopBarActions />
            <IconButton
              onClick={() => window.dispatchEvent(new CustomEvent('refreshPage'))}
              aria-label="Atualizar"
              sx={{
                background: 'linear-gradient(135deg, #00897b 0%, #26a69a 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(0,137,123,0.3)',
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ mt: 2, flexShrink: 0 }}>
          <IdentityCarousel />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mt: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
